import { useCallback, useState, type ReactNode } from 'react';
import ApproveEventModal from '../components/ApproveEventModal';
import { api } from '../lib/api';
import { clearSwrCacheByPrefix } from '../lib/swrCache';
import type { ExtractedEvent } from '../types';
import { toast } from '../lib/dialogs';

interface UseEventApprovalOptions {
  /**
   * Called after the backend confirms the approval. Use this to prune the
   * event from the queue, close detail panels, etc. The hook already
   * clears the `events:` SWR cache for you.
   */
  onApproved?: (event: ExtractedEvent, flagForAiTraining: boolean) => void;
}

interface UseEventApprovalResult {
  /**
   * Open the in-app approve confirmation modal for the given event.
   * Caller is responsible for closing any other open modals first if needed.
   */
  promptApprove: (event: ExtractedEvent) => void;
  /**
   * Render this somewhere in the page tree. The modal is hidden until
   * `promptApprove` is called.
   */
  modal: ReactNode;
}

/**
 * Shared admin "approve" flow for the review queue. Mirror of
 * `useEventRejection` — encapsulates:
 *   1. The `approvingEvent` state
 *   2. The `<ApproveEventModal>` mount with its "Approve only" /
 *      "Approve & flag for AI auto-approval" choices
 *   3. The `POST /api/mgmt/events/:id/approve` API call (with the AI flag)
 *   4. Clearing the shared SWR `events:` cache so other windows refetch
 *
 * Pages provide an `onApproved` callback to do their own local cleanup
 * (e.g. reload the queue list, focus the next row).
 */
export function useEventApproval(opts: UseEventApprovalOptions = {}): UseEventApprovalResult {
  const { onApproved } = opts;
  const [approvingEvent, setApprovingEvent] = useState<ExtractedEvent | null>(null);

  const promptApprove = useCallback((event: ExtractedEvent) => {
    setApprovingEvent(event);
  }, []);

  const handleConfirm = useCallback(
    async (event: ExtractedEvent, flagForAiTraining: boolean) => {
      setApprovingEvent(null);
      try {
        try {
          await api.post(`/api/mgmt/events/${encodeURIComponent(event.rowKey)}/approve`, {
            flaggedForAiTraining: flagForAiTraining,
          });
        } catch (err) {
          // Tolerate 404 the same way rejection does — the event may have
          // been deleted by a background job between caching and click.
          const status = (err as { status?: number } | null)?.status;
          if (status !== 404) throw err;
        }
        clearSwrCacheByPrefix('events:');
        onApproved?.(event, flagForAiTraining);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        toast.error(`Could not approve event: ${msg}`);
      }
    },
    [onApproved],
  );

  const modal = (
    <ApproveEventModal
      event={approvingEvent}
      onClose={() => setApprovingEvent(null)}
      onConfirm={handleConfirm}
    />
  );

  return { promptApprove, modal };
}
